import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {
  ButtonStyleParams,
  CellApiModule,
  CellPosition,
  CellSelectionModule,
  CellStyleModule,
  CheckboxEditorModule,
  CheckboxStyleParams,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ClipboardModule,
  Column,
  ColumnApiModule,
  ColumnAutoSizeModule,
  ColumnHoverModule,
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  CoreParams,
  CustomEditorModule,
  CustomFilterModule,
  DateEditorModule,
  DateFilterModule,
  DragAndDropModule,
  EventApiModule,
  ExcelExportModule,
  ExternalFilterModule,
  FiltersToolPanelModule,
  FindModule,
  GetCellRendererInstancesParams,
  GridApi,
  GridStateModule,
  GroupFilterModule,
  HighlightChangesModule,
  InputStyleParams,
  IRowNode,
  IServerSideSelectionState,
  LargeTextEditorModule,
  LicenseManager,
  LocaleModule,
  ModuleRegistry,
  MultiFilterModule,
  NavigateToNextCellParams,
  NewFiltersToolPanelModule,
  NumberEditorModule,
  NumberFilterModule,
  PaginationModule,
  PinnedRowModule,
  PivotModule,
  ProcessCellForExportParams,
  QuickFilterModule,
  RenderApiModule,
  RowApiModule,
  RowAutoHeightModule,
  RowDragModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  RowNodeTransaction,
  RowNumbersModule,
  RowSelectionModule,
  RowStyleModule,
  ScrollApiModule,
  SelectEditorModule,
  SendToClipboardParams,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  SetFilterModule,
  SideBarModule,
  StatusBarModule,
  TabStyleParams,
  TabToNextCellParams,
  TextEditorModule,
  TextFilterModule,
  TooltipModule,
  ValidationModule,
  ValueCacheModule
} from "ag-grid-enterprise";
import ClientCache from "@/utils/login/ClientCache.ts";
import Translator from "@/utils/common/Translator.ts";
import NaMaConfirmationDialog from "@/utils/forms/NaMaConfirmationDialog.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import MetaData from "@/utils/common/MetaData.ts";
import {WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import EditScreenGridHandler from "@/utils/common/EditScreenGridHandler.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import BaseDispatcher from "@/utils/presenters/BaseDispatcher.ts";
import GridAction from "@/utils/constants/GridAction.ts";
import {useThemeStore} from "@/stores/ThemeDataStore.ts";
import {ColDef, ColGroupDef, GridOptions, ICellRendererParams, ManagedGridOptionKey, ToolPanelDef} from "ag-grid-community";
import {ComponentPublicInstance, MaybeRef, ShallowRef, TemplateRef, toValue, watch} from "vue";
import {useMessagesStore} from "@/stores/ToastDataStore.ts";
import {useNamaBreakpoints} from "@/composables/useNamaBreakpoints.ts";
import AGGridSettingsPanel from "@/components/EditView/widgets/AGGridSettingsPanel.vue";
import IdsAndConstants from "@/utils/UIConstants/IdsAndConstants.ts";
import AbsPresenter from "@/utils/common/AbsPresenter.ts";
import CurrentUserUtils from "@/utils/login/CurrentUserUtils.ts";
import FieldMetaData = namaTypes.FieldMetaData;
import EntityReferenceData = namaTypes.EntityReferenceData;
import NaMaText = namaTypes.NaMaText;

export default class AGGridUtils {
  public static cellCopyMethods = Object.freeze({
    JSON: "systemJson",
    CODE: "code",
    NAME: "name"
  })
  static autoLoadDisabledMsg:NaMaText = {arabicText: 'تم إيقاف التحميل التلقائي، أضف فلترة لتنزيل السطور', englishText: 'Auto Load Disabled, Add Filters To Load Rows'};
  static cellCopyTypeMethod = 'agCellCopyMethod';
  static suppressRowDragKey: ManagedGridOptionKey = 'suppressRowDrag';
  static suppressMovableColumnsKey: ManagedGridOptionKey = 'suppressMovableColumns';
  static defaultAGGridThemeOptions: Partial<CoreParams & ButtonStyleParams & CheckboxStyleParams & TabStyleParams & InputStyleParams> = {
    accentColor: "#2196F3",
    backgroundColor: "var(--nama-ag-grid-background-color)",
    borderColor: "var(--nama-ag-grid-border-color)",
    borderRadius: 4,
    browserColorScheme: "var(--nama-dark-mode-enabled)",
    cellTextColor: "var(--nama-ag-grid-cell-text-color)",
    chromeBackgroundColor: "var(--nama-ag-grid-chrome-background-color)",
    columnBorder: true,
    fontFamily: "inherit",
    fontSize: "var(--nama-compact-mode-font-size)",
    foregroundColor: "var(--nama-ag-grid-foreground-color)",
    headerFontSize: "var(--nama-compact-mode-font-size)",
    headerHeight: "var(--nama-compact-mode-header-height)",
    headerRowBorder: true,
    headerTextColor: "var(--nama-ag-grid-header-text-color)",
    listItemHeight: "var(--nama-compact-mode-row-height)",
    oddRowBackgroundColor: "var(--nama-ag-grid-odd-row-background-color)",
    rowBorder: true,
    rowHeight: "var(--nama-compact-mode-row-height)",
    spacing: "var(--nama-compact-mode-spacing)",
    tooltipBackgroundColor: "var(--nama-ag-grid-tooltip-background-color)",
    wrapperBorderRadius: 8,
    checkboxCheckedBackgroundColor: "var(--nama-ag-grid-checkBox-checked-color)",
  };

  static registerGridModules() {
    LicenseManager.setLicenseKey("Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-081411}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Namasoft}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{namasoft}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{namasoft}_need_to_be_licensed___{namasoft}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{12_September_2026}____[v3]_[0102]_MTc4OTE2NzYwMDAwMA==55058c1b16e3ae3d9dd76df690380ed0");
    ModuleRegistry.registerModules([
      ExternalFilterModule,
      ColumnAutoSizeModule,
      RowNumbersModule,
      ColumnHoverModule,
      PinnedRowModule,
      RowAutoHeightModule,
      RowStyleModule,
      PaginationModule,
      RowDragModule,
      CellStyleModule,
      HighlightChangesModule,
      TooltipModule,
      TextFilterModule,
      NumberFilterModule,
      DateFilterModule,
      CustomFilterModule,
      RowSelectionModule,
      TextEditorModule,
      LargeTextEditorModule,
      SelectEditorModule,
      NumberEditorModule,
      DateEditorModule,
      CheckboxEditorModule,
      CustomEditorModule,
      LocaleModule,
      DragAndDropModule,
      ValueCacheModule,
      GridStateModule,
      ColumnApiModule,
      RowApiModule,
      CellApiModule,
      ScrollApiModule,
      RenderApiModule,
      EventApiModule,
      ClientSideRowModelApiModule,
      ClientSideRowModelModule,
      SetFilterModule,
      MultiFilterModule,
      CellSelectionModule,
      ValidationModule,
      RowGroupingModule,
      RowGroupingPanelModule,
      GroupFilterModule,
      PivotModule,
      SideBarModule,
      ColumnsToolPanelModule,
      FiltersToolPanelModule,
      ColumnMenuModule,
      ContextMenuModule,
      StatusBarModule,
      ExcelExportModule,
      ClipboardModule,
      ServerSideRowModelApiModule,
      ServerSideRowModelModule,
      NewFiltersToolPanelModule,
      FindModule,
      QuickFilterModule
    ]);
  }

  private static getDensityValues() {
    const mode = useNamaFlagsStore().densityMode;
    if (mode === 'Ultra')
      return {spacing: '1px', fontSize: '14px', rowHeight: '24px', headerHeight: '28px'};
    if (mode === 'Compact')
      return {spacing: '3px', fontSize: '14px', rowHeight: '30px', headerHeight: '34px'};
    return {spacing: '8px', fontSize: '16px', rowHeight: '36px', headerHeight: '40px'};
  }

  static updateThemeVars() {
    const isDark = useNamaFlagsStore().darkMode;
    const themeStore = useThemeStore();
    const v = this.getDensityValues();
    document.body.style.setProperty('--nama-compact-mode-spacing', v.spacing);
    document.body.style.setProperty('--nama-compact-mode-font-size', v.fontSize);
    document.body.style.setProperty('--nama-compact-mode-row-height', v.rowHeight);
    document.body.style.setProperty('--nama-compact-mode-header-height', v.headerHeight);
    document.body.style.setProperty('--nama-dark-mode-enabled', isDark ? "dark" : "light");
    document.body.style.setProperty('--nama-ag-grid-background-color', isDark ? themeStore.getBackgroundDeepDarkColor() : themeStore.getBackgroundDeepLightColor());
    document.body.style.setProperty('--nama-ag-grid-odd-row-background-color', isDark ? themeStore.getBackgroundBrightDarkColor() : themeStore.getBackgroundBrightLightColor());
    document.body.style.setProperty('--nama-ag-grid-border-color', isDark ? "#535353" : "#e8e8e8");
    document.body.style.setProperty('--nama-ag-grid-cell-text-color', isDark ? themeStore.getFontDarkColor() : themeStore.getFontLightColor());
    document.body.style.setProperty('--nama-ag-grid-chrome-background-color', isDark ? "#383838" : "#DEE2E6");
    document.body.style.setProperty('--nama-ag-grid-foreground-color', isDark ? "#D6D6D6" : "#3C3C3C");
    document.body.style.setProperty('--nama-ag-grid-header-text-color', isDark ? "#FFFFFF" : "#394452");
    document.body.style.setProperty('--nama-ag-grid-tooltip-background-color', isDark ? "#1E1E1E" : "#F2F2F2");
    document.body.style.setProperty('--nama-dataGridFreeLine', "#00FF00" + (isDark ? "A0" : ""));
    document.body.style.setProperty('--nama-dataGridSlaveLine', "#808080A0");
    document.body.style.setProperty('--nama-dataGridReturnedRow', "#FF0000" + (isDark ? "A0" : ""));
    document.body.style.setProperty('--nama-dataGridSentRow', "#00FF00" + (isDark ? "A0" : ""));
    document.body.style.setProperty('--nama-dataGridInvalidSentRow', "#FF0000" + (isDark ? "A0" : ""));
    document.body.style.setProperty('--nama-ag-grid-checkBox-checked-color', isDark ? themeStore.getPrimaryDarkColor() : themeStore.getPrimaryColor());
    let guiSettings = ClientCache.getCustomGUISettings();
    if (guiSettings?.gridColoursMap) {
      let gridColoursMap = guiSettings.gridColoursMap;
      for (const gridColorKey in gridColoursMap)
        document.body.style.setProperty('--nama-' + gridColorKey, gridColoursMap[gridColorKey] +
          (isDark && gridColoursMap[gridColorKey].startsWith("#") ? 'A0' : ''));
    }
  }

  static updateCompactVars() {
    const v = this.getDensityValues();
    const isDark = useNamaFlagsStore().darkMode;
    document.body.style.setProperty('--nama-compact-mode-spacing', v.spacing);
    document.body.style.setProperty('--nama-compact-mode-row-height', v.rowHeight);
    document.body.style.setProperty('--nama-compact-mode-header-height', v.headerHeight);
    document.body.style.setProperty('--nama-compact-mode-font-size', v.fontSize);
    document.body.style.setProperty('--nama-dark-mode-enabled', isDark ? "dark" : "light");
  }

  static getCurrentPageData(gridApi: GridApi): any[] {
    const pageSize = gridApi.paginationGetPageSize();
    const currentPage = gridApi.paginationGetCurrentPage();
    const startRow = currentPage * pageSize;
    const endRow = startRow + pageSize;
    const currentPageData: any[] = [];
    for (let i = startRow; i < endRow; i++) {
      const rowNode = gridApi.getDisplayedRowAtIndex(i);
      if (rowNode)
        currentPageData.push(rowNode.data);
    }
    return currentPageData;
  }

  static fetchSelectedRows(gridApi: GridApi): any[] {
    let selectedRows: any[] = [];
    if (gridApi.getGridOption("rowModelType") === "serverSide" && gridApi.getServerSideSelectionState()) {
      /*TODO:: To be changed while working with groups*/
      let state = gridApi.getServerSideSelectionState() as IServerSideSelectionState;
      if (state.selectAll) {
        let pageData = this.getCurrentPageData(gridApi) ?? [];
        selectedRows = pageData.filter(l => !state.toggledNodes.includes(l['namaRowIndex']));
      } else if (state.toggledNodes && state.toggledNodes.length) {
        let pageData = this.getCurrentPageData(gridApi) ?? [];
        selectedRows = pageData.filter(l => state.toggledNodes.includes(l['namaRowIndex']));
      }
    }
    if (selectedRows.length)
      return selectedRows;
    return gridApi.getSelectedRows();
  }

  static navigateToNextCellAndStartEditing(gridApi: GridApi) {
    gridApi.tabToNextCell()
    let focusedCell = gridApi.getFocusedCell();
    if (focusedCell)
      gridApi.startEditingCell({
        rowIndex: focusedCell.rowIndex,
        colKey: focusedCell.column.getColId()
      });
  }

  static sendToClipboard(params: SendToClipboardParams) {
    if (!params.data)
      return;
    if (params.data.startsWith("<")) {
      const html = new Blob([params.data], {type: "text/html"});
      const plain = new Blob([params.data], {type: "text/plain"});
      const data = [new ClipboardItem({"text/html": html, "text/plain": plain})];
      navigator.clipboard.write(data);
    } else {
      navigator.clipboard.writeText(params.data);
    }
  }

  static onNavigationToNextCell(presenter: WidgetsPresenter, gridId: string, handler?: EditScreenGridHandler): (params: NavigateToNextCellParams) => CellPosition | null {
    return (params: NavigateToNextCellParams) => {
      if (params.nextCellPosition)
        return params.nextCellPosition;
      let arrowStart = Translator.isLanguageRTL() ? 'ArrowRight' : 'ArrowLeft';
      let focusedCell = params.previousCellPosition;
      let columns = params.api.getAllDisplayedColumns();
      if (params.key === arrowStart && focusedCell.rowIndex > 0)
        return {
          rowIndex: focusedCell.rowIndex - 1,
          column: columns[columns.length - 1],
          rowPinned: undefined
        }
      let arrowEnd = Translator.isLanguageRTL() ? 'ArrowLeft' : 'ArrowRight';
      let arrowEndOrDown = [arrowEnd, 'ArrowDown'].includes(params.key);
      if (!arrowEndOrDown)
        return params.nextCellPosition;
      if (params.key === arrowEnd && focusedCell.rowIndex !== params.api.getDisplayedRowCount() - 1)
        return {
          rowIndex: focusedCell.rowIndex + 1,
          column: columns[0],
          rowPinned: undefined
        }
      if (!this.canAppendRow(presenter, gridId))
        return params.nextCellPosition;
      if (params.api.isAnyFilterPresent()) {
        NaMaConfirmationDialog.okCancelConfirmationDialog(
          Translator.translateId('areYouSureToAddLinesThatMayNotBeDisplayed'), true).then(res => {
          if (!res)
            return;
          const api = params.api;
          const rowCountBeforeAppend = api.getDisplayedRowCount();
          this.appendRowAfterNavigation(api, focusedCell, presenter, gridId, handler);
          if (rowCountBeforeAppend < api.getDisplayedRowCount()) {
            api.setFocusedCell(focusedCell.rowIndex + 1, focusedCell.column, focusedCell.rowPinned);
          }
        });
        return params.nextCellPosition;
      }
      const node = this.appendRowAfterNavigation(params.api, focusedCell, presenter, gridId, handler);
      return {
        rowIndex: node?.add[0].rowIndex!,
        column: arrowEnd === params.key ? columns[0] : focusedCell.column,
        rowPinned: undefined
      }
    }
  }

  static appendRowAfterNavigation(api: GridApi, focusedCell: CellPosition, presenter: WidgetsPresenter, gridId: string, handler?: EditScreenGridHandler)
    : RowNodeTransaction | null | undefined {
    let rowData = api.getDisplayedRowAtIndex(focusedCell.rowIndex)!;
    let rows: any[] = NamaCommon.getFieldValue(presenter.fetchRecord(), gridId);
    let realRowIndex = ObjectChecker.findIndexOf(rows, (l) => l.id === rowData.data.id) || 0;
    let node = this.createRow(rowData.data, api, realRowIndex + 1, presenter, gridId);
    const dispatcher = presenter.fetchDispatcher();
    if (dispatcher && handler) {
      dispatcher.afterRowInserted(handler, realRowIndex + 1, false).then(() => {
        const rows = NamaCommon.getFieldValue(presenter.fetchRecord(), gridId);
        api.applyTransaction({update: rows});
      });
    }
    return node;
  }

  static createRow(currentRowData: Record<string, any>, api: GridApi, rowIndex: number, presenter: WidgetsPresenter, gridId: string) {
    if (this.isEmptyLine(currentRowData))
      return;
    let copy = {};
    let id = ClientCache.syncNextGUIDId();
    copy[CommonFieldIds.ID] = id;
    copy["g2Id"] = id;
    let rows = NamaCommon.getFieldValue(presenter.fetchRecord(), gridId);
    let node = api.applyTransaction({add: [copy], addIndex: rowIndex});
    api.applyTransaction({update: rows});
    rows.splice(rowIndex, 0, copy);
    return node;
  }

  static async asyncCreateRow(currentRowData: Record<string, any>, rowIndex: number, handler: EditScreenGridHandler) {
    if (this.isEmptyLine(currentRowData))
      return;
    const presenter = handler.presenter;
    const gridId = handler.gridId;
    const api = handler.api;
    if (ObjectChecker.isEmptyOrNull(api))
      return;
    const copy = {};
    let id = await ClientCache.asyncNextGUIDId()
    copy[CommonFieldIds.ID] = id;
    copy["g2Id"] = id;
    let rows = NamaCommon.getFieldValue(presenter.fetchRecord(), gridId);
    const node = api.applyTransaction({add: [copy], addIndex: rowIndex});
    rows.splice(rowIndex, 0, copy);
    return node;
  }

  static isEmptyLine(lineData: Record<string, any>, treatZeroAsEmpty?: boolean): boolean {
    if (!lineData)
      return true;
    for (const key in lineData) {
      if (!Object.prototype.hasOwnProperty.call(lineData, key))
        continue;
      if (key === CommonFieldIds.ID || key === 'g2Id')
        continue;
      const val = lineData[key];
      if (val == null)
        continue;
      if (typeof val === 'string') {
        const trimmed = val.trim();
        if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined')
          continue;
        return false;
      }
      if (!ObjectChecker.isDeepEmpty(val, treatZeroAsEmpty))
        return false;
    }
    return true;
  }

  static canAppendRow(presenter: WidgetsPresenter, gridId: string) {
    if (presenter.isFieldDisabled(gridId))
      return false;
    let gridMetadata = MetaData.findFieldMetadata(gridId, presenter.fetchMetadata());
    if (gridMetadata && (gridMetadata.systemField || gridMetadata.guiField))
      return false;
    const dispatcher = presenter.fetchDispatcher();
    if (dispatcher) {
      if (dispatcher.isAppendRowDisabled(gridId))
        return false;
      return !dispatcher.isViewOnlyMode();
    }
    return true;
  }

  static canCopyRow(dispatcher: BaseDispatcher, gridId: string): boolean {
    return !dispatcher.isCopyRowDisabled(gridId);
  }

  static canDeleteRow(handler: EditScreenGridHandler): boolean {
    if (handler.presenter.isFieldDisabled(handler.gridId))
      return false;
    let gridMetadata = MetaData.findFieldMetadata(handler.gridId, handler.metadata);
    if (gridMetadata && (gridMetadata.systemField || gridMetadata.guiField))
      return false;
    if (handler.dispatcher.isDeleteRowDisabled(handler.gridId))
      return false;
    return true;
  }

  static OnTabToNextCell(presenter: WidgetsPresenter, gridId: string, handler?: EditScreenGridHandler): (params: TabToNextCellParams) => CellPosition | boolean {
    return (params: TabToNextCellParams) => {
      let api = params.api;
      let currentCell = params.previousCellPosition;
      let currentRowIndex = currentCell.rowIndex;
      let currentColId = currentCell.column.getColId();
      if (params.backwards)
        return this.fetchPreviousEditableCell(currentRowIndex, currentColId, presenter, gridId, api);
      if (params.nextCellPosition || !AGGridUtils.canAppendRow(presenter, gridId))
        return this.fetchNextEditableCell(currentRowIndex, currentColId, params.api, presenter, gridId);
      if (params.api.isAnyFilterPresent()) {
        NaMaConfirmationDialog.okCancelConfirmationDialog(
          Translator.translateId('areYouSureToAddLinesThatMayNotBeDisplayed'), true).then(res => {
          if (!res)
            return;
          const api = params.api;
          const rowCountBeforeAppend = api.getDisplayedRowCount();
          AGGridUtils.appendRowAfterNavigation(api, currentCell, presenter, gridId, handler);
          if (rowCountBeforeAppend < api.getDisplayedRowCount()) {
            const nextEditableCell = this.fetchNextEditableCell(currentCell.rowIndex, currentColId, params.api, presenter, gridId);
            if (typeof nextEditableCell === "object") {
              const cellPosition = nextEditableCell as CellPosition;
              api.setFocusedCell(cellPosition.rowIndex, cellPosition.column, cellPosition.rowPinned);
            }
          }
        });
        return true;
      }
      AGGridUtils.appendRowAfterNavigation(params.api, currentCell, presenter, gridId, handler);
      return this.fetchNextEditableCell(currentRowIndex, currentColId, params.api, presenter, gridId);
    }
  }

  static fetchPreviousEditableCell(currentRow: number, colId: string, presenter: WidgetsPresenter, gridId: string, gridApi: GridApi) {
    let rows = NamaCommon.getFieldValue(presenter.fetchRecord(), gridId);
    let columns = gridApi.getAllDisplayedColumns();
    let startCol = ObjectChecker.findIndexOf(columns, l => l.getColId() === colId)! - 1;
    if (startCol < 0) {
      startCol = columns.length - 1;
      if (currentRow >= rows.length - 1)
        currentRow -= 1;
    }
    for (let r = currentRow; r >= 0; r--) {
      for (let col = startCol; col >= 0; col--) {
        let currentCol = columns[col];
        if (!presenter.isColumnDisabled(rows[r].id, gridId, gridId + "." + currentCol.getColId()) && currentCol.getColId() !== "colNumber")
          return {
            rowIndex: r,
            column: currentCol,
            rowPinned: undefined
          }
      }
      startCol = columns.length - 1;
    }
    return true;
  }

  static fetchNextEditableCell(currentRowIndex: number, colId: string, api: GridApi, presenter: WidgetsPresenter, gridId: string) {
    let columns = api.getAllDisplayedColumns();
    let startCol = ObjectChecker.findIndexOf(columns, l => l.getColId() === colId)! + 1;
    if (startCol >= columns.length) {
      startCol = 0;
      if (currentRowIndex < api.getDisplayedRowCount() - 1)
        currentRowIndex += 1;
    }
    for (let r = currentRowIndex; r < api.getDisplayedRowCount(); r++) {
      for (let col = startCol; col < columns.length; col++) {
        let currentCol = columns[col];
        if (!presenter.isColumnDisabled(api.getDisplayedRowAtIndex(r)?.data.id, gridId, gridId + "." + currentCol.getColId()) && currentCol.getColId() !== "colNumber")
          return {
            rowIndex: r,
            column: currentCol,
            rowPinned: undefined
          }
      }
      startCol = 0;
    }
    return true;
  }

  static setBinaryFieldsToNullIfNeeded(copy: Record<string, any>, presenter: WidgetsPresenter, gridId: string) {
    let metaData = presenter.fetchMetadata();
    (metaData.fieldsMetaData ?? []).filter(l => l && l.fieldType === FieldType.Binary && l.fieldId && l.fieldId.startsWith(gridId + ".")).forEach(l => {
      copy[l.fieldId!.substring(gridId.length + 1)] = null;
    });
  }

  static isFocusedCellEditing(api: GridApi<any>, focusedCell: CellPosition) {
    return api.getEditingCells().length && focusedCell && focusedCell.rowIndex == api.getEditingCells()[0].rowIndex;
  }

  static async asyncCreateRows(currentRowData: Record<string, any>, api: GridApi, rowIndex: number, isDuplicate: boolean, presenter: WidgetsPresenter, gridId: string, count?: number): Promise<RowNodeTransaction<any> | null | undefined> {
    count = count ?? 1;
    let newElements: any[] = [];
    for (let i = 0; i < count; i++) {
      let copy = {};
      if (isDuplicate) {
        copy = NamaCommon.cloneObject(currentRowData);
        AGGridUtils.setBinaryFieldsToNullIfNeeded(copy, presenter, gridId);
      }
      let id = await ClientCache.asyncNextGUIDId();
      copy[CommonFieldIds.ID] = id;
      copy["g2Id"] = id;
      newElements.push(copy);
    }
    let rows = NamaCommon.getFieldValue(presenter.fetchRecord(), gridId);
    const nodes = api.applyTransaction({add: newElements, addIndex: rowIndex});
    api.applyTransaction({update: rows});
    rows.splice(rowIndex, 0, ...newElements);
    return nodes
  }

  static processCellForClipboard(params: ProcessCellForExportParams) {
    const value = params.value;
    if (!value)
      return value;
    if (ObjectChecker.isString(value))
      return value;
    const copyType = localStorage.getItem(AGGridUtils.cellCopyTypeMethod);
    switch (copyType) {
      case AGGridUtils.cellCopyMethods.CODE:
        return Translator.getCode(value);
      case AGGridUtils.cellCopyMethods.NAME:
        return Translator.getNameForFields(value);
      default:
        return IdsAndConstants.NM_JSON_PREFIX + NamaCommon.stringify(value);
    }
  }

  static processCellFromClipboard(params: ProcessCellForExportParams) {
    if (!params.node?.data)
      return;
    let row = params.node.data;
    let metadata = params.column.getColDef().context as FieldMetaData;
    const path = metadata.fieldId?.substring(metadata.fieldId?.indexOf(".") + 1) ?? '';
    const cellValue = NamaCommon.getFieldValue(row, path);
    return AGGridUtils.parseClipboardToObjectOrString(params.value, params.column, cellValue);
  }

  public static parseClipboardToObjectOrString(rowValue: any, column: Column, oldValue?: any) {
    let value = rowValue;
    if (!value)
      return value;
    let metadata = column.getColDef().context as FieldMetaData;
    if (typeof value === "string") {
      if (value.startsWith(IdsAndConstants.NM_JSON_PREFIX))
        try {
          value = JSON.parse(value.substring(IdsAndConstants.NM_JSON_PREFIX.length));
        } catch (ignore) {
        }
    }
    if ([FieldType.Reference, FieldType.Genericreference].includes(metadata.fieldType ?? "")) {
      if (typeof value === "object") {
        if ((metadata.fieldType === FieldType.Reference && value.entityType === metadata.referencedEntityType) ||
          ClientCache.calcAllowedValuesForEntityType(metadata).includes(value.entityType))
          return value;
        else
          return "";
      } else {
        if (metadata.fieldType === FieldType.Reference) {
          return {entityType: metadata.referencedEntityType, code: value, findFromServer: true};
        } else {
          if (oldValue && oldValue.entityType)
            return {entityType: oldValue.entityType, code: value, findFromServer: true};
          return;
        }
      }
    } else if ([FieldType.Enum, FieldType.EntityType, FieldType.FeatureID].includes(metadata.fieldType ?? "")){
      let allowedValues = ClientCache.calcAllowedValuesForEntityType(metadata);
      if (allowedValues && !allowedValues.includes(value))
        return;
    }

    if ([FieldType.Decimal, FieldType.Integer, FieldType.Long].includes(metadata.fieldType ?? "")) {
      if (ObjectChecker.isString(value)) {
        const formattedValue = value.replaceAll(",", "");
        return Number(formattedValue);
      }
    }
    return value;
  }

  static async removeLine(currentRowData: Record<string, any>, api: GridApi, handler: EditScreenGridHandler) {
    let focusedCell = api.getFocusedCell();
    api.applyTransaction({remove: [currentRowData]});
    let rows: any[] = NamaCommon.getFieldValue(handler.presenter.fetchRecord(), handler.gridId);
    let realRowIndex = ObjectChecker.findIndexOf(rows, l => l.id === currentRowData.id)!;
    if (ObjectChecker.isNullOrUndefined(realRowIndex))
      return;
    rows.splice(realRowIndex, 1);
    rows = NamaCommon.getFieldValue(handler.presenter.fetchRecord(), handler.gridId);
    api.applyTransaction({update: rows});
    if (rows.length === 0 && AGGridUtils.canAppendRow(handler.presenter, handler.gridId)) {
      let newLine = {id: ClientCache.syncNextGUIDId()};
      api.applyTransaction({add: [newLine], addIndex: 0});
      rows.push(newLine);
      await handler.dispatcher.prevalidateAllFieldsOfGridAtLine(handler.listVisibleColumns(), handler.gridId, 0);
      let columns = api.getAllDisplayedColumns();
      api.setFocusedCell(0, columns[0]);
    }
    await handler.dispatcher.asyncGridAction(handler, realRowIndex, GridAction.DeleteLine, currentRowData);
    rows = NamaCommon.getFieldValue(handler.presenter.fetchRecord(), handler.gridId);
    api.applyTransaction({update: rows});
    if (focusedCell && focusedCell?.rowIndex === api.getDisplayedRowCount())
      api.setFocusedCell(focusedCell?.rowIndex - 1, focusedCell.column);
  }

  static getSuppressRowDragFromStorage(gridId: string): boolean {
    const rowDragStr = localStorage.getItem(this.suppressRowDragKey + "_" + gridId);
    if (!rowDragStr)
      return true;
    return JSON.parse(rowDragStr);
  }

  static toggleRowDragWithCallback(api: GridApi, gridId: string, cb: (newValue: boolean) => void) {
    const newValue = !api.getGridOption(this.suppressRowDragKey);
    api!.setGridOption(this.suppressRowDragKey, newValue);
    localStorage.setItem(this.suppressRowDragKey + "_" + gridId, JSON.stringify(newValue))
    cb(!newValue);
  }

  static getSuppressColumnDragFromStorage(gridId: string): boolean {
    const suppressColumnDragStr = localStorage.getItem(this.suppressMovableColumnsKey + "_" + gridId);
    if (!suppressColumnDragStr)
      return false;
    return JSON.parse(suppressColumnDragStr);
  }

  static toggleColumnDragWithCallback(api: GridApi, gridId: string, cb: (newValue: boolean) => void) {
    const newValue: boolean = !api.getGridOption(this.suppressMovableColumnsKey);
    api!.setGridOption(this.suppressMovableColumnsKey, newValue);
    localStorage.setItem(this.suppressMovableColumnsKey + "_" + gridId, JSON.stringify(newValue))
    cb(newValue);
  }

  static fetchFocusedCellContext(focusedCell: CellPosition, api: GridApi | undefined): Record<string, any> | null {
    const firstRowNode: IRowNode<any> | undefined = api?.getDisplayedRowAtIndex(0);
    const params: GetCellRendererInstancesParams<any> = {columns: [focusedCell.column], rowNodes: firstRowNode ? [firstRowNode] : undefined};
    const instances = api?.getCellRendererInstances(params);
    if (instances && instances.length > 0) {
      let instance = instances[0];
      if (instance?.$?.exposed) {
        return instance.$.exposed
      }
    }
    return null;
  }

  public static getDefaultAgGridOptions(): GridOptions {
    return {
      ...this.getSideBarConfig(),
      ...this.getStatusBarConfigForServerSide(),
      ...this.getExcelExportConfigs(),
      defaultColDef: {
        // Apply row striping for Excel export (TableStyleMedium2 effect)
        cellClassRules: this.getExcelExportCellClassRules()
      }
    };
  }

  static excelStringStyleId = "stringType";
  static excelHeaderStyleId = "header";
  static excelOddRowStyleId = "oddRow";
  static excelEvenRowStyleId = "evenRow";

  public static getExcelExportConfigs(): Pick<GridOptions, "excelStyles" | "defaultExcelExportParams"> {
    return {
      excelStyles: [
        {
          dataType: "String",
          id: this.excelStringStyleId
        },
        {
          // TableStyleMedium2 header style - blue background, white bold text
          id: this.excelHeaderStyleId,
          interior: {
            color: "#4472C4",
            pattern: "Solid"
          },
          font: {
            color: "#FFFFFF",
            bold: true
          },
          borders: {
            borderBottom: {color: "#2F5496", lineStyle: "Continuous", weight: 1},
            borderLeft: {color: "#2F5496", lineStyle: "Continuous", weight: 1},
            borderRight: {color: "#2F5496", lineStyle: "Continuous", weight: 1},
            borderTop: {color: "#2F5496", lineStyle: "Continuous", weight: 1}
          }
        },
        {
          // TableStyleMedium2 odd row style - light blue background
          id: this.excelOddRowStyleId,
          interior: {
            color: "#D9E2F3",
            pattern: "Solid"
          }
        },
        {
          // TableStyleMedium2 even row style - white background
          id: this.excelEvenRowStyleId,
          interior: {
            color: "#FFFFFF",
            pattern: "Solid"
          }
        }
      ],
      defaultExcelExportParams: {
        headerRowHeight: 25,
        rowHeight: 20
      }
    }
  }

  // Cell class rules for Excel export row striping (TableStyleMedium2 effect)
  public static getExcelExportCellClassRules(): Record<string, (params: any) => boolean> {
    return {
      [this.excelOddRowStyleId]: (params) => params.node?.rowIndex !== undefined && params.node.rowIndex % 2 === 0,
      [this.excelEvenRowStyleId]: (params) => params.node?.rowIndex !== undefined && params.node.rowIndex % 2 === 1
    };
  }

  public static getDefaultAgGridOptionsForEditScreen(): GridOptions {
    return {
      ...this.getSideBarConfig(),
      ...this.getStatusBarConfigForClientSide(),
    };
  }

  public static getSideBarConfig(): Pick<GridOptions, "enableFilterHandlers" | "sideBar"> {
    return {
      enableFilterHandlers: true,
      sideBar: {
        toolPanels: [
          this.getFilterToolPanel(),
          this.getColumnsToolPanel(),
          this.getSettingsToolPanel()
        ]
      }
    };
  }

  public static getTableDashboardSideBarConfig(): Partial<GridOptions> {
    return {
      enableFilterHandlers: true,
      pivotRowTotals: "after",
      sideBar: {
        toolPanels: [
          this.getFilterToolPanel(),
          this.getColumnsToolPanelForPivotMode()
        ]
      },
      ...this.getStatusBarConfigForClientSide(),
      ...this.getExcelExportConfigs()
    };
  }

  public static getFilterToolPanel(): ToolPanelDef {
    return {
      id: "filters-new",
      labelKey: "",
      labelDefault: "",
      iconKey: "filter",
      toolPanel: "agNewFiltersToolPanel",
    };
  }

  public static getColumnsToolPanel(): ToolPanelDef {
    return {
      id: 'columns',
      labelDefault: "",
      labelKey: "",
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
      }
    };
  }

  public static getColumnsToolPanelForPivotMode(): ToolPanelDef {
    return {
      id: 'columns',
      labelDefault: "",
      labelKey: "",
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel'
    };
  }

  public static getSettingsToolPanel(): ToolPanelDef {
    return {
      id: 'namaSettings',
      labelDefault: "",
      labelKey: "",
      iconKey: 'menu',
      toolPanel: AGGridSettingsPanel as any,
    };
  }

  public static getStatusBarConfigForServerSide(): Pick<GridOptions, "statusBar"> {
    return {
      statusBar: {
        statusPanels: [
          {statusPanel: "agAggregationComponent"}
        ]
      }
    };
  }

  public static getStatusBarConfigForClientSide(): Pick<GridOptions, "statusBar"> {
    return {
      statusBar: {
        statusPanels: [
          {statusPanel: "agAggregationComponent"},
          {statusPanel: "agTotalRowCountComponent"}
        ]
      }
    };
  }

  static isBulkAction(event: KeyboardEvent): boolean {
    return event.shiftKey && event.key == "Enter";
  }

  static dispatchBulkEventFor(element: TemplateRef<ComponentPublicInstance | HTMLElement | null>) {
    let el: HTMLElement;
    const synthetic = new KeyboardEvent("keydown", {
      key: "Enter",
      ctrlKey: true,
      bubbles: true,
      cancelable: true,
      composed: true
    });
    const elValue = element.value;
    if (!elValue)
      return;

    if (this.isQRef(elValue))
      el = elValue.$el
    else
      el = elValue;

    el?.dispatchEvent(synthetic);
  }

  static isQRef(el: ComponentPublicInstance | HTMLElement): el is ComponentPublicInstance {
    return !!el && "$el" in el;
  }

  static async appendNewLine(handler: EditScreenGridHandler, focusedCellId?: string | Column) {
    const api = handler.api;
    if (!api)
      return;
    let focusedRowData = this.fetchFocusedRowData(api);
    if (!focusedRowData)
      return;
    if (AGGridUtils.isEmptyLine(focusedRowData)) {
      useMessagesStore().pushMessageStr("Could not insert new line while you have an empty line");
      return;
    }
    let rows: any[] = NamaCommon.getFieldValue(handler.presenter.fetchRecord(), handler.gridId);
    let realRowIndex = ObjectChecker.findIndexOf(rows, l => l.id === focusedRowData.id)!;
    await AGGridUtils.asyncCreateRow(focusedRowData, realRowIndex + 1, handler);
    await this.afterRowInserted(handler, realRowIndex + 1, false, api);
    focusedCellId = focusedCellId ?? api.getFocusedCell()?.column;
    if (focusedCellId) {
      api.clearFocusedCell();
      api.setFocusedCell(realRowIndex + 1, focusedCellId)
    }
  }

  static fetchFocusedRowData(api: GridApi | undefined) {
    if (!api)
      return;
    let focusedCell = api.getFocusedCell();
    if (!focusedCell)
      return;
    let focusedRow = api.getDisplayedRowAtIndex(focusedCell.rowIndex);
    if (!focusedRow || !focusedRow.data)
      return;
    return focusedRow.data;
  }

  static async afterRowInserted(handler: EditScreenGridHandler, realRowIndex: number, isDuplicate: boolean, api: GridApi) {
    await handler.dispatcher.afterRowInserted(handler, realRowIndex, isDuplicate);
    let rows = NamaCommon.getFieldValue(handler.presenter.fetchRecord(), handler.gridId);
    api.applyTransaction({update: rows});
  }

  static dotPlaceHolder = "___";

  static encodeColId(colId?: string) {
    return colId?.replaceAll(".", AGGridUtils.dotPlaceHolder) ?? '';
  }

  static decodeColId(colId?: string): string {
    return colId?.replaceAll(AGGridUtils.dotPlaceHolder, ".") ?? '';
  }

  static createRenderedDivWithTitle(params: ICellRendererParams): HTMLElement {
    const valueFormatted = params.valueFormatted ?? '';
    return AGGridUtils.createFindHighlightedDiv(params, valueFormatted);
  }

  static createFindHighlightedDiv(params: ICellRendererParams, displayValue: string): HTMLElement {
    const div = document.createElement("div");
    div.title = displayValue;  //SQL code may be contain "" make div crash
    const parts = params.api.findGetParts({node: params.node, column: params.column!, value: displayValue});
    if (parts.length > 0) {
      for (const part of parts) {
        const span = document.createElement("span");
        span.textContent = part.value;
        if (part.activeMatch) {
          span.classList.add("ag-find-active-match");
        } else if (part.match) {
          span.classList.add("ag-find-match");
        }
        div.appendChild(span);
      }
    } else {
      div.textContent = displayValue;
    }
    return div;
  }

  static getFindHighlightedHtml(api: GridApi, node: any, column: any, displayValue: string): string {
    const parts = api.findGetParts({node, column, value: displayValue});
    if (parts.length === 0) return '';
    let html = '';
    for (const part of parts) {
      const escaped = part.value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      if (part.activeMatch) {
        html += `<span class="ag-find-active-match">${escaped}</span>`;
      } else if (part.match) {
        html += `<span class="ag-find-match">${escaped}</span>`;
      } else {
        html += escaped;
      }
    }
    return html;
  }

  static findColDef(colDefs: any[], colId: string): ColDef | undefined {
    for (const col of colDefs) {
      if (col.colId === colId) return col

      if (col.children?.length) {
        const found = this.findColDef(col.children, colId)
        if (found) return found
      }
    }
  }

  static isAgGridBuiltInColumn(columnId: string | undefined) {
    if (ObjectChecker.isEmptyOrNull(columnId))
      return true;
    return columnId.startsWith("ag-Grid-")
  }

  static getRowNumberOptions(): GridOptions['rowNumbers'] {
    if (useNamaBreakpoints().isMobile.value)
      return false;
    return {width: 35};
  }

  static watchRowNumberOptions(gridApi: ShallowRef<GridApi | undefined>) {
    const {isMobile} = useNamaBreakpoints();
    watch(isMobile, (mobile) => {
      gridApi.value?.setGridOption('rowNumbers', mobile ? false : {width: 35});
    }, {immediate: true});
  }

  static childrenOfCol(l: ColDef | ColGroupDef): ColDef[] | ColGroupDef[] {
    if (!(l as ColGroupDef).children)
      return [l];
    return (l as ColGroupDef).children.flatMap(child => this.childrenOfCol(child));
  }

  static fetchDisplayedColumns(gridApi: GridApi | undefined, columnDefs?: (ColDef | ColGroupDef)[]) {
    let columns: string[];
    if (columnDefs?.length)
      columns = columnDefs.flatMap(l => AGGridUtils.childrenOfCol(l)).filter(l =>
        !AGGridUtils.isAgGridBuiltInColumn(l.colId)).map(l => l.colId!);
    else if (gridApi)
      columns = gridApi.getAllDisplayedColumns().filter(l =>
        !AGGridUtils.isAgGridBuiltInColumn(l.getColId())).map(l => l.getColId());
    else
      columns = [];
    columns = columns.map(AGGridUtils.decodeColId);
    return columns;
  }

  static syncAgGridRows(presenter: WidgetsPresenter, gridId: string, gridApi: MaybeRef<GridApi | null | undefined>) {
    const givenRows = NamaCommon.getFieldValue(presenter.fetchRecord(), gridId)
    if (!givenRows || !Array.isArray(givenRows))
      return;
    const api = toValue(gridApi);
    if (!api)
      return;
    const rowsToRemove: EntityReferenceData[] = [];
    const rowsToAdd: EntityReferenceData[] = [];
    const rowsToUpdate: EntityReferenceData[] = [];
    let addIndex: number | undefined;
    for (let i = 0; i < givenRows.length; i++) {
      const dRow = givenRows[i];
      const rowNode = api.getRowNode(dRow.id);
      if (ObjectChecker.isEmptyOrNull(rowNode)) {
        if (addIndex === undefined)
          addIndex = i;
        rowsToAdd.push(dRow);
      } else if (ObjectChecker.areNotDeepEqual(dRow, rowNode.data))
        rowsToUpdate.push(dRow)
    }
    api.forEachNode(node => {
      if (!givenRows.find((r: any) => r.id === node.data?.id))
        rowsToRemove.push(node.data);
    });
    api.applyTransaction({add: rowsToAdd, addIndex, remove: rowsToRemove, update: rowsToUpdate})
    if (ObjectChecker.isNotEmptyOrNull(addIndex))
      api.ensureIndexVisible(addIndex + rowsToAdd.length - 1);
  }

  static fetchDefaultPageSize(presenter: AbsPresenter) {
    let pageSize: string | undefined = presenter.fetchDefaultPageSize();
    if (pageSize)
      return CurrentUserUtils.calcDefaultPageSize(pageSize);
    return 25;
  }
}
